import { fetchWithAuth, urlBuilder } from "@/lib/api/utils";
import type { Message } from "@/lib/api/types";

/**
 * Emit every complete newline-delimited record in `buffer` via `onMessage`,
 * returning the trailing partial record (text after the last "\n"). Shared by
 * the streaming and buffered paths so both honour the same NDJSON framing.
 */
function drainNdjsonBuffer(
  buffer: string,
  onMessage: (message: string) => void
): string {
  let boundary = buffer.indexOf("\n");
  while (boundary !== -1) {
    onMessage(buffer.slice(0, boundary));
    buffer = buffer.slice(boundary + 1);
    boundary = buffer.indexOf("\n");
  }
  return buffer;
}

export async function generateChatResponse(
  messages: Message[],
  sessionId: string | null,
  onMessage: (message: string) => void
): Promise<void> {
  const response = await fetchWithAuth(urlBuilder("/generate"), {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify({
      session_id: sessionId ?? null,
      messages: messages,
    }),
  });

  if (!response.ok) {
    throw new Error("Network response was not ok");
  }

  // Buffered fallback: some Teams webviews (WebView2 / older mobile) don't
  // expose a streaming body / ReadableStream.getReader(). Rather than fail,
  // read the whole response and replay it line-by-line — same NDJSON contract,
  // just without incremental rendering. (Verified path for MA-135.)
  if (!response.body || typeof response.body.getReader !== "function") {
    const rest = drainNdjsonBuffer(await response.text(), onMessage);
    if (rest.length > 0) {
      onMessage(rest);
    }
    return;
  }

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let done = false;
  let buffer = "";

  while (!done) {
    const { value, done: readerDone } = await reader.read();
    done = readerDone;
    if (value) {
      buffer += decoder.decode(value, { stream: true });
      buffer = drainNdjsonBuffer(buffer, onMessage);
    }
  }

  if (buffer.length > 0) {
    onMessage(buffer); // Handle any remaining text in the buffer
  }
}
